import { useQuery } from "@apollo/client";
import { useTaxonomyContext } from "../contexts/Taxonomy/TaxonomyContext";
import { useTamSpendCategories } from "./useTamSpendCategories";
import { useWorkspaceStore } from "../store/workspace/WorkspaceStore";
import { SIMPLIFIED_PRODUCT_ID_QUERY } from "../apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "../utils/consts";
import { getLoggedUserProfile } from "../utils/auth";
import { useFeatureFlag } from "./useLaunchDarkly";
import { useSignedInUserCompany } from "./useSignedInUserCompany";
import { useTenantPreferences } from "./useTenantPreferences";
import { useMemo } from "react";

export const useCopilotContext = () => {
  const workspaceDraft = useWorkspaceStore((state) => state.draft);
  const userEmail = getLoggedUserProfile()?.email;
  const projectSegmentImprovementsEnabled = useFeatureFlag(
    "maProjectSegmentImprovements",
    false
  );
  // New, INDEPENDENT flag — turning this on without
  // maProjectSegmentImprovements still ships the new fields. See HIP-12516.
  const contextEnrichmentEnabled = useFeatureFlag(
    "maCopilotContextEnrichment",
    false
  );

  // Both hooks called unconditionally (Rules of Hooks); each internally skips
  // network calls when its enabled flag is false.
  const { data: tenantPrefs } = useTenantPreferences(contextEnrichmentEnabled);
  const { data: signedInUserCompany } = useSignedInUserCompany(
    contextEnrichmentEnabled
  );

  const {
    spendCategoriesLoading,
    hgIndustriesLoading,
    countriesLoading,
    productCategoriesLoading,
    hgIndustries,
    countries,
    productCategories,
  } = useTaxonomyContext();
  const tamSpendCategories = useTamSpendCategories(
    workspaceDraft?.configuration.tam.spendType
  );

  const competitorIds =
    workspaceDraft?.configuration.competitorsProductIds || [];
  const icpProductIds = workspaceDraft?.configuration.icp?.productIds || [];

  const { data: competitorProductsData } = useQuery(
    SIMPLIFIED_PRODUCT_ID_QUERY,
    {
      context: { clientName: TAXONOMY_CLIENT },
      variables: { ids: competitorIds },
      skip: !workspaceDraft,
    }
  );

  const { data: icpProductsData } = useQuery(SIMPLIFIED_PRODUCT_ID_QUERY, {
    context: { clientName: TAXONOMY_CLIENT },
    variables: { ids: icpProductIds },
    skip:
      !workspaceDraft ||
      icpProductIds.length === 0 ||
      !projectSegmentImprovementsEnabled,
  });

  const products =
    competitorProductsData && competitorProductsData.product.data;
  const icpProducts = icpProductsData?.product.data;

  const context = useMemo(() => {
    if (
      !workspaceDraft ||
      spendCategoriesLoading ||
      hgIndustriesLoading ||
      countriesLoading ||
      !products
    ) {
      return undefined;
    }

    if (
      projectSegmentImprovementsEnabled &&
      (productCategoriesLoading || !userEmail)
    ) {
      return undefined;
    }

    const improvedFields = projectSegmentImprovementsEnabled
      ? {
          workspace_name: workspaceDraft.name,
          workspace_description: workspaceDraft.description,
          workspace_owner_email: workspaceDraft.owner_email,
          user_email: userEmail,
          self_product_categories: productCategories
            .filter(({ id }) =>
              workspaceDraft.configuration.productCategoryIds.includes(id)
            )
            .map(({ id, name }) => ({ id, name })),
          icp_products: (icpProducts || []).map(
            ({ id, name }: { id: number; name: string }) => ({ id, name })
          ),
          ...(workspaceDraft.configuration.icp?.customerMatch
            ? {
                icp_customer_match_file: {
                  file_id:
                    workspaceDraft.configuration.icp.customerMatch.fileId,
                  file_name:
                    workspaceDraft.configuration.icp.customerMatch.fileName,
                },
              }
            : {}),
        }
      : {};

    // Enrichment fields ride OUTSIDE `improvedFields` so they're gated only by
    // `maCopilotContextEnrichment`, independent of `maProjectSegmentImprovements`.
    const enrichmentFields = {
      ...(tenantPrefs ? { tenant_preferences: tenantPrefs } : {}),
      ...(signedInUserCompany
        ? { signed_in_user_company: signedInUserCompany }
        : {}),
    };

    return {
      workspace_id: workspaceDraft.id,
      active_url: window.location.href,
      spend_category_filter: tamSpendCategories
        .filter(({ id }) =>
          workspaceDraft.configuration.tam.spendCategoryIds.includes(id)
        )
        .map(({ id, name }) => ({ id, name })),
      industry_filter: hgIndustries
        .filter(({ id }) =>
          workspaceDraft.configuration.sam.industry.ids.includes(id)
        )
        .map(({ id, name }) => ({ id, name })),
      geography_filter: countries
        .filter(({ alpha2 }) =>
          workspaceDraft.configuration.sam.geography.countryIds.includes(alpha2)
        )
        .map(({ alpha2, name }) => ({ id: alpha2, name })),
      employee_range_filter: workspaceDraft.configuration.som.employeeRange,
      revenue_range_filter: workspaceDraft.configuration.som.revenueRange,
      competitor_products_filter: products.map(
        ({ id, name }: { id: number; name: string }) => ({
          id,
          name,
        })
      ),
      ...(workspaceDraft.configuration.whitespace
        ? {
            whitespace: {
              file_id: workspaceDraft.configuration.whitespace.fileId,
              file_name: workspaceDraft.configuration.whitespace.fileName,
            },
          }
        : {}),
      ...improvedFields,
      ...enrichmentFields,
    };
  }, [
    workspaceDraft,
    userEmail,
    projectSegmentImprovementsEnabled,
    spendCategoriesLoading,
    hgIndustriesLoading,
    countriesLoading,
    productCategoriesLoading,
    tamSpendCategories,
    hgIndustries,
    countries,
    productCategories,
    products,
    icpProducts,
    tenantPrefs,
    signedInUserCompany,
  ]);

  return context;
};
